// Thin client for Zebra Browser Print's local REST API.
//
// Browser Print runs on the packer's machine and exposes an HTTPS service
// on https://localhost:9101 (default). Our web app talks to it directly
// from the browser using fetch — no backend involvement. Browser Print
// gates access by an "Accepted Hosts" list (see Zebra settings dialog);
// our app's origin must be on that list.
//
// See: https://www.zebra.com/us/en/products/software/barcode-printers/link-os/browser-print.html

const BASE = 'https://localhost:9101';

// Fetch the currently-selected default device (the printer the app should
// send to). Returns the raw device object Browser Print expects on /write,
// or null if Browser Print isn't reachable / no device is configured.
export const getDefaultPrinter = async () => {
  try {
    const r = await fetch(`${BASE}/default?type=printer`, { method: 'GET' });
    if (!r.ok) return null;
    const body = await r.json();
    // Some Browser Print versions return { printer: {...} }, others return
    // the device object directly. Handle both.
    return body?.printer || body || null;
  } catch {
    return null;
  }
};

// Send raw ZPL to the given device. Returns true on success.
export const sendZpl = async (device, zpl) => {
  const r = await fetch(`${BASE}/write`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device, data: zpl }),
  });
  return r.ok;
};

// One-shot helper: print a batch of ZPL jobs to the default printer.
// Concatenates them into a single write so labels advance without pauses.
export const printLabels = async (zplList) => {
  const device = await getDefaultPrinter();
  if (!device) {
    throw new Error(
      'Zebra Browser Print not reachable. Is it running? ' +
      'Is this domain in Browser Print\u2019s Accepted Hosts?'
    );
  }
  const combined = zplList.join('\n');
  const ok = await sendZpl(device, combined);
  if (!ok) throw new Error('Browser Print rejected the print job.');
  return device;
};
